/**
 * AgeGateModal — the neutral, write-once date-of-birth capture the 426
 * `age_required` gate pops for an existing NULL-DOB user.
 *
 * NEUTRAL BY DESIGN (the documented compliance decision): a plain "Date of
 * birth" field with NO "you must be 13+" hint. Surfacing the threshold would
 * teach a child the self-attestation workaround (enter a fake adult DOB to
 * dodge the gate). The copy explains WHY we ask (to set up the right
 * experience) without naming an age.
 *
 * WRITE-ONCE + MINIMIZATION: submits to POST /auth/age, which captures the DOB
 * exactly once. We never persist or log the date client-side; the backend does
 * not echo it back. A 409 (already set, e.g. a value landed from another tab)
 * is treated as SUCCESS — the durable signal is on file, so the original
 * request can proceed; we resolve and let the interceptor retry.
 *
 * ACCESSIBILITY: a real modal dialog via the shared <Modal> primitive —
 * role="dialog" aria-modal, labelled + described, initial focus on the date
 * input, focus TRAPPED within (Tab/Shift+Tab cycle), Escape dismisses (which
 * rejects the capture so the gated call surfaces its own state rather than
 * hanging), and focus RESTORE on close. A native <input type="date"> gives
 * keyboard + screen-reader + platform-picker support for free.
 *
 * WHILE THE SAVE IS IN FLIGHT (components/ui/busyForm.js): nothing dismisses
 * the gate. Escape and a scrim press used to, with "Not now" disabled beside
 * them, and that rejected every request waiting on the gate while POST
 * /auth/age went on to save the date. The date field and Continue are never
 * natively disabled either (a browser drops focus from a control the moment it
 * is): the field turns read-only, Continue aria-disabled, and whichever
 * started the save keeps focus. A date the server rejects, or a submit with no
 * date, puts focus in the field the message is about.
 *
 * FOCUS RESTORE (WCAG 2.4.3): <Modal> returns focus to wherever it was when the
 * modal opened — but this dialog is INTERCEPTOR-driven (a 426 pops it; there is
 * no trigger element the user clicked), so the captured element is usually
 * `<body>`. In that case (or if the opener was unmounted) it falls back to the
 * page's `#main-content` skip-nav target so a keyboard/SR user lands somewhere
 * deterministic instead of being stranded at `<body>` after Escape / "Not now".
 * (This is why <Modal> factors out useRestoreFocus — it was born here.)
 *
 * This component is presentational: the parent <AgeConsentGate> owns the
 * open/closed state and passes `onResolved` (DOB set → release the waiters) and
 * `onDismiss` (rejected → let the original request fail). Escape + scrim-click
 * both route to `onDismiss` via <Modal onClose>.
 */
import { useRef, useState } from 'react';
// Bundle de-bloat (audit r3): direct Spinner/Button/Modal imports, not the
// `../components/ui` barrel. AgeGateModal is statically imported by
// AgeConsentGate, which App.js eager-imports — so this module is in the
// ENTRY graph. A barrel import here would re-expose every barrel member's
// (and its CSS's) side effects to the entry chunk. Direct paths keep it to
// exactly Spinner + Button + Modal.
import Spinner from '../components/ui/Spinner';
import PendingStatus from '../components/ui/PendingStatus';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { busyForm, useRefusedFieldFocus } from '../components/ui/busyForm';
import { setDateOfBirth } from './coppaApi';
import './coppa.css';

export default function AgeGateModal({ onResolved, onDismiss }) {
  const [dob, setDob] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldError, setFieldError] = useState(null);

  const inputRef = useRef(null);
  const focusRefused = useRefusedFieldFocus(inputRef);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldError(null);
    if (!dob) {
      setFieldError('Please enter your date of birth to continue.');
      // A message about the field puts the student in the field.
      focusRefused();
      return;
    }
    setSubmitting(true);
    try {
      await setDateOfBirth(dob);
      onResolved?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        // WRITE-ONCE: already on file. Treat as done — the durable signal
        // exists, so the original request can proceed.
        onResolved?.();
        return;
      }
      if (status === 422) {
        setFieldError(
          'That date doesn’t look right. Please check it and try again.',
        );
        // The date is what to fix, so a student who submitted with Continue
        // goes back to it, once the message has rendered.
        focusRefused();
      } else {
        // System/network error — red is correct here (NOT a minor-state).
        // Nothing about the date is wrong: focus stays on whichever control
        // the student pressed, ready for the retry.
        setError('We couldn’t save that just now. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const form = busyForm({ busy: submitting, onSubmit: handleSubmit });

  return (
    // Escape + scrim-click both dismiss (reject the capture so the gated call
    // surfaces its own state), except while the date is being saved: `busy`
    // holds the gate open then. Initial focus lands on the date input.
    <Modal
      overlayClassName="coppa-overlay"
      className="coppa-modal"
      labelledBy="coppa-age-title"
      describedBy="coppa-age-desc"
      onClose={() => onDismiss?.()}
      busy={submitting}
      initialFocusRef={inputRef}
    >
      <div className="coppa-modal__icon" aria-hidden="true">🗓️</div>
      <h2 id="coppa-age-title" className="coppa-modal__title">
        One quick thing
      </h2>
      <p id="coppa-age-desc" className="coppa-modal__desc">
        Add your date of birth so we can set up the right experience for you.
        You’ll only need to do this once.
      </p>

      <form {...form.formProps} className="coppa-form" noValidate>
        <div className="coppa-field">
          <label htmlFor="coppa-age-dob" className="coppa-field__label">
            Date of birth
          </label>
          {/* COPPA: an autofilled adult birthday on a shared family device
              would decide the consent path, so no autofill ("bday" is WCAG
              1.3.5's token): a deliberate exception, recorded for the owner. */}
          <input
            id="coppa-age-dob"
            ref={inputRef}
            type="date"
            className="coppa-field__input"
            autoComplete="off"
            value={dob}
            onChange={(e) => {
              setDob(e.target.value);
              setFieldError(null);
            }}
            required
            {...form.fieldProps}
            aria-invalid={fieldError ? 'true' : undefined}
            aria-describedby={fieldError ? 'coppa-age-dob-error' : undefined}
          />
          {fieldError && (
            <p
              id="coppa-age-dob-error"
              className="coppa-field__error"
              role="alert"
            >
              {fieldError}
            </p>
          )}
        </div>

        {error && (
          <div className="error-message" role="alert">{error}</div>
        )}

        <div className="coppa-modal__actions">
          <Button variant="primary" {...form.submitProps}>
            {submitting ? (
              <>
                <Spinner size="sm" decorative /> Saving…
              </>
            ) : (
              'Continue'
            )}
          </Button>
          {/* Natively disabled for the wait: a save starts from the date field
              or from Continue, and that is where focus is. (Except in a
              browser where a pointer press moves no focus: see busyForm.js.) */}
          <Button
            variant="ghost"
            onClick={() => onDismiss?.()}
            disabled={submitting}
          >
            Not now
          </Button>
        </div>
        {/* The in-button Spinner is decorative (aria-hidden); this sibling live
            region carries the pending cue so a screen reader announces it. */}
        <PendingStatus pending={submitting}>Saving…</PendingStatus>
      </form>
    </Modal>
  );
}
